// Pairing logic for container→stop tethers, kept apart from the layer that
// draws them so it can be tested without pulling in deck.gl — same split as
// containerMarkerColors.js and ContainerMarkerLayer.js.
import get from 'lodash/get'

/** `[longitude, latitude]`, deck.gl order. */
export type TetherPosition = [number, number]

/** The marker shape both ends are read from — geojson as the map surfaces build it. */
export type TetherFeature = {
  geometry?: { coordinates?: unknown } | null
  properties?: Record<string, unknown> | null
}

export type ContainerTether = {
  id: string
  source: TetherPosition
  target: TetherPosition
}

const toCoordinates = (feature: TetherFeature): TetherPosition | null => {
  const coordinates = get(feature, 'geometry.coordinates')
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null
  const [longitude, latitude] = coordinates as unknown[]
  return Number.isFinite(longitude) && Number.isFinite(latitude) ? [longitude as number, latitude as number] : null
}

/**
 * Pairs containers to stops on `workOrderId` — the only link the geojson
 * carries. Containers reference the work order, never the stop marker's id.
 *
 * Endpoints are read from the features as given, so passing the rendered
 * (offset, mid-drag) features makes the tether track what is on screen rather
 * than the underlying coordinates.
 */
export const buildContainerTetherData = (
  containerFeatures: TetherFeature[] = [],
  stopFeatures: TetherFeature[] = []
): ContainerTether[] => {
  const stopByWorkOrderId = new Map<string, TetherFeature>()
  stopFeatures.forEach(stop => {
    const id = get(stop, 'properties.id') as string | undefined
    if (id) stopByWorkOrderId.set(id, stop)
  })

  return containerFeatures.flatMap(container => {
    // A snap review's "old location" ghost is a second drawing of a container
    // that already has its own tether, not a second container to tie up.
    if (get(container, 'properties._isSnapGhost')) return []
    const stop = stopByWorkOrderId.get(get(container, 'properties.workOrderId') as string)
    if (!stop) return []

    const source = toCoordinates(container)
    const target = toCoordinates(stop)
    if (!source || !target) return []

    return [{ id: get(container, 'properties.id') as string, source, target }]
  })
}
